import React, { useState } from "react"
import { saveAs } from "file-saver"
import { Timestamp } from "firebase/firestore"
import { useCollection } from "../../../hooks/useCollection"
import { collection, addDoc, query, where, getDocs, updateDoc, doc } from "firebase/firestore"
import { db } from "../../../firebase/config"

const GetClients = () => {
  // Fetch clients and sales users.
  const { data: clientsData } = useCollection("clients")
  const { data: usersData } = useCollection("users", ["group", "array-contains", "sales"])
  const [missingClients, setMissingClients] = useState([])
  const [invoices, setInvoices] = useState([])
  const [isUploading, setIsUploading] = useState(false)
  const [unknownInvoices, setUnknownInvoices] = useState([])
  // This state holds per-client selected manager (keyed by a unique client identifier, e.g. registration).
  const [selectedClientManagers, setSelectedClientManagers] = useState({})

  // ----- XML Conversion Functions -----
  const xmlNodeToObject = (node) => {
    if (node.children.length === 0) {
      const text = node.textContent.trim()
      return text === "" ? null : text
    }
    const obj = {}
    for (let i = 0; i < node.children.length; i++) {
      const child = node.children[i]
      const childObj = xmlNodeToObject(child)
      if (obj[child.nodeName] !== undefined) {
        if (!Array.isArray(obj[child.nodeName])) {
          obj[child.nodeName] = [obj[child.nodeName]]
        }
        obj[child.nodeName].push(childObj)
      } else {
        obj[child.nodeName] = childObj
      }
    }
    return obj
  }

  const handleXmlFileChange = (e) => {
    const fileReader = new FileReader()
    fileReader.onload = (event) => {
      const xmlString = event.target.result
      const parser = new DOMParser()
      const xmlDoc = parser.parseFromString(xmlString, "text/xml")
      const dokumentsNodes = xmlDoc.getElementsByTagName("Dokuments")
      const docs = []
      for (let i = 0; i < dokumentsNodes.length; i++) {
        docs.push(xmlNodeToObject(dokumentsNodes[i]))
      }
      const outputBlob = new Blob([JSON.stringify(docs, null, 2)], {
        type: "application/json",
      })
      saveAs(outputBlob, "converted_file.json")
    }
    fileReader.readAsText(e.target.files[0])
  }

  // ----- JSON Invoice Transformation -----
  const toTimestamp = (dateStr) => {
    const date = new Date(dateStr)
    return Timestamp.fromDate(date)
  }

  const transformData = (inputData) => {
    // Prepare lookup for clients as before...
    const clientsByName = {}
    const clientsByReg = {}

    clientsData.forEach((client) => {
      if (client.client) clientsByName[client.client.toLowerCase()] = client
      if (client.reg) clientsByReg[client.reg.toLowerCase()] = client
    })

    const missingClientsMap = new Map()

    const transformedData = inputData.map((entry) => {
      // Determine matched client logic as before...
      const personalNameKey = entry.PersonalName ? entry.PersonalName.toLowerCase() : ""
      const personalNmrNrKey = entry.PersonalNmrNr ? entry.PersonalNmrNr.toLowerCase() : ""

      const matchedClient = clientsByName[personalNameKey] || clientsByReg[personalNmrNrKey]

      let clientData
      if (!matchedClient) {
        const key = `${personalNameKey}-${personalNmrNrKey}`
        if (!missingClientsMap.has(key)) {
          missingClientsMap.set(key, {
            PersonalName: entry.PersonalName,
            PersonalNmrNr: entry.PersonalNmrNr,
          })
        }
        clientData = {
          client: entry.PersonalName,
          reg: entry.PersonalNmrNr,
          vat: entry.PersonalPvnNr,
          user: "unknown",
          userEmail: "unknown",
        }
      } else {
        clientData = matchedClient
      }

      // Ensure Kontejums is always an array.
      const kontejums = Array.isArray(entry.Kontejums) ? entry.Kontejums : [entry.Kontejums]

      // Split kontejums into valid (Cena !== "0.00") and ignored (Cena === "0.00")
      const validItems = kontejums.filter((item) => item.Cena !== "0.00")
      const ignoredItems = kontejums.filter((item) => item.Cena === "0.00")

      // Calculate totals:
      // totalNetoSum: sum of Summa from valid items
      // totalVat: sum of Summa from ignored items
      const totalNetoSum = parseFloat(
        validItems.reduce((sum, item) => sum + parseFloat(item.Summa || 0), 0).toFixed(2)
      )
      const totalVat = parseFloat(
        ignoredItems.reduce((sum, item) => sum + parseFloat(item.Summa || 0), 0).toFixed(2)
      )
      const totalSum = parseFloat((totalNetoSum + totalVat).toFixed(2))

      // Map prices only for validItems
      const prices = validItems.map((item) => ({
        vat: 0,
        code: 0,
        hasManualCount: false,
        unit: "thous.",
        cAccount: 6110,
        hasProductComment: false,
        key: 1,
        weight: 0,
        sum: parseFloat(item.Summa || 0),
        hasChangableSpace: false,
        price: parseFloat(item.Summa || 0),
        dCurrency: "Eur",
        product: item.Text,
        productComment: "",
        cName: "Ieņēmumi no pamatdarbības produkcijas un pakalpojumu pārdošanas",
        kCurrency: "Eur",
        count: 1,
        dAccount: 2310,
        dName: "Norēķini ar pircējiem un pasūtītājiem",
      }))

      return {
        payUntilTimestamp: toTimestamp(entry.HronoDatums),
        invoiceDate: toTimestamp(entry.HronoDatums),
        payUntil: "",
        userId: clientData.user,
        manufacturer: {
          deliveryPC: "LV-1029",
          vat: "LV40203468389",
          legalPC: "LV-1009",
          logoUrl:
            "https://firebasestorage.googleapis.com/v0/b/managers-testing.appspot.com/o/manufacturers%2Fartlabel%2Flogo.jpg?alt=media&token=44219060-e59b-4d7f-bbae-6abeda6ebe1c",
          invoicePrefix: "AR",
          deliveryAddress: "Lazdu iela 16 D",
          legalAddress: "Pērnavas iela 61-1",
          bankCode: "HABALV22",
          accountNumber: "LV16HABA0551054382294",
          id: "DodjV1wBRUlrKpmLPgRe",
          comment: "ART LABEL SIA",
          legalCity: "Rīga",
          company: "ART LABEL SIA",
          reg: "40203468389",
          bankName: '"Swedbank" AS',
          deliveryCity: "Rīga",
        },
        deleted: false,
        orderReferenceId: [],
        totalData: {
          totalSum, // neto sum + vat sum
          totalProfitDiscount: 0,
          totalNetoSum, // sum of valid items (Cena !== "0.00")
          totalVat, // sum of ignored items (Cena === "0.00")
        },
        invoiceUsed: true,
        client: clientData,
        user: clientData.userEmail,
        prices,
        hronodId: parseInt(entry.HronoID),
        clientId: matchedClient ? matchedClient.id : "",
        documentForComment: "",
        manufacturerDeliveryAddress: "Lazdu iela 16 D, Rīga, LV-1029",
        created: toTimestamp(entry.HronoDatums),
        documentDate: toTimestamp(entry.HronoDatums),
        pdfLanguage: "Latvian",
        commentText: "",
        downloadUrl: "",
        docPrefix: entry.Rezerve1 || "ART",
        clientDeliveryAddress: "Lazdu iela 16 D, Rīga, LV-1029",
        docNumber: parseInt(entry.HronoID),
        resaleInvoice: false,
        pdfDate: toTimestamp(entry.HronoDatums),
        documentFor: "clientPickup",
        payUntill: toTimestamp(entry.HronoDatums),
        customInvoice: true,
        currency: "EUR",
      }
    })

    setMissingClients(Array.from(missingClientsMap.values()))
    setInvoices(transformedData)
    const outputBlob = new Blob([JSON.stringify(transformedData, null, 2)], {
      type: "application/json",
    })
    saveAs(outputBlob, "transformed_invoices.json")
  }

  const handleInvoiceFileChange = (e) => {
    const fileReader = new FileReader()
    fileReader.onload = (event) => {
      const inputData = JSON.parse(event.target.result)
      transformData(inputData)
    }
    fileReader.readAsText(e.target.files[0])
  }

  // ----- Upload to Firebase -----
  const handleUploadInvoices = async () => {
    if (!invoices.length) return
    setIsUploading(true)
    try {
      const invoicesRef = collection(db, "invoices")
      for (const invoice of invoices) {
        await addDoc(invoicesRef, invoice)
      }
      alert("Invoices uploaded successfully!")
    } catch (error) {
      console.error("Error uploading invoices: ", error)
      alert("Error uploading invoices.")
    }
    setIsUploading(false)
  }

  // ----- Fetch Unknown Invoices from Firebase -----
  const handleFetchUnknownInvoices = async () => {
    try {
      const invoicesRef = collection(db, "invoices")
      const q = query(invoicesRef, where("userId", "==", "unknown"))
      const querySnapshot = await getDocs(q)
      const fetchedInvoices = []
      querySnapshot.forEach((doc) => {
        fetchedInvoices.push({ id: doc.id, ...doc.data() })
      })
      setUnknownInvoices(fetchedInvoices)
      // Reset per-client manager selections.
      setSelectedClientManagers({})
    } catch (error) {
      console.error("Error fetching unknown invoices: ", error)
      alert("Error fetching unknown invoices.")
    }
  }

  // ----- Group Unknown Invoices by Client -----
  const groupInvoicesByClient = (invoices) => {
    return invoices.reduce((groups, invoice) => {
      const key =
        invoice.client && invoice.client.reg
          ? invoice.client.reg
          : invoice.client && invoice.client.client
          ? invoice.client.client
          : "unknown"
      if (!groups[key]) {
        groups[key] = { client: invoice.client, invoices: [] }
      }
      groups[key].invoices.push(invoice)
      return groups
    }, {})
  }

  // ----- Update All Invoices for a Specific Client Group -----
  const handleUpdateClientGroup = async (clientKey, groupInvoices) => {
    const selUserId = selectedClientManagers[clientKey] || "unknown"
    let newData
    if (selUserId === "unknown") {
      newData = { clientId: "unknown", user: "unknown", userId: "unknown" }
    } else {
      const selectedUser = usersData.find((u) => u.id === selUserId)
      if (!selectedUser) return
      newData = {
        clientId: selectedUser.id,
        user: selectedUser.email,
        userId: selectedUser.id,
      }
    }
    try {
      for (const invoice of groupInvoices) {
        const invoiceDocRef = doc(db, "invoices", invoice.id)
        await updateDoc(invoiceDocRef, newData)
      }
      setUnknownInvoices((prev) =>
        prev.map((inv) =>
          groupInvoices.some((gi) => gi.id === inv.id) ? { ...inv, ...newData } : inv
        )
      )
      alert(`Invoices for client ${clientKey} updated successfully!`)
    } catch (error) {
      console.error("Error updating invoices: ", error)
      alert("Error updating invoices.")
    }
  }

  // ----- Basic Styling -----
  const containerStyle = {
    maxWidth: "800px",
    margin: "20px auto",
    padding: "20px",
    boxShadow: "0 0 10px rgba(0,0,0,0.1)",
    borderRadius: "8px",
    fontFamily: "Arial, sans-serif",
    backgroundColor: "#f9f9f9",
  }

  const sectionStyle = {
    marginBottom: "30px",
  }

  const headerStyle = {
    textAlign: "center",
    color: "#333",
  }

  const inputStyle = {
    display: "block",
    margin: "10px auto",
  }

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: "10px",
  }

  const thTdStyle = {
    border: "1px solid #ccc",
    padding: "8px",
    textAlign: "left",
  }

  // Group unknown invoices by client.
  const groupedInvoices = groupInvoicesByClient(unknownInvoices)

  return (
    <div style={containerStyle}>
      <h1 style={headerStyle}>Client & File Converter</h1>

      {/* Invoice Transformation Section */}
      <section style={sectionStyle}>
        <h2>Transform Invoices (JSON)</h2>
        <p>
          Upload your JSON file to transform invoices. Missing clients (without duplicates) will be
          shown below.
        </p>
        <input
          style={inputStyle}
          type="file"
          accept="application/json"
          onChange={handleInvoiceFileChange}
        />
        {missingClients.length > 0 && (
          <div>
            <h3>Missing Clients</h3>
            <ul>
              {missingClients.map((client, index) => (
                <li key={index}>
                  {client.PersonalName} - {client.PersonalNmrNr}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* Upload to Firebase Section */}
      <section style={sectionStyle}>
        <h2>Upload Transformed Invoices to Firebase</h2>
        <p>
          If you have transformed invoices, you can now upload them to the Firestore "invoices"
          collection.
        </p>
        {invoices.length > 0 ? (
          <button
            onClick={handleUploadInvoices}
            style={{ ...inputStyle, cursor: "pointer" }}
            disabled={isUploading}>
            {isUploading ? "Uploading..." : "Upload Invoices to Firebase"}
          </button>
        ) : (
          <p>No transformed invoices available. Please transform a JSON file first.</p>
        )}
      </section>

      {/* Fetch and Update Unknown Invoices Grouped by Client */}
      <section style={sectionStyle}>
        <h2>Fetch Unknown Invoices & Update by Client</h2>
        <p>Click the button below to fetch all invoices from Firestore where userId = "unknown".</p>
        <button onClick={handleFetchUnknownInvoices} style={{ ...inputStyle, cursor: "pointer" }}>
          Fetch Unknown Invoices
        </button>
        {Object.keys(groupedInvoices).length > 0 && (
          <div>
            <h3>Unknown Invoices Grouped by Client</h3>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thTdStyle}>Client Name</th>
                  <th style={thTdStyle}>Registration Number</th>
                  <th style={thTdStyle}>Invoice Count</th>
                  <th style={thTdStyle}>Select Manager</th>
                  <th style={thTdStyle}>Action</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(groupedInvoices).map(([clientKey, group]) => (
                  <tr key={clientKey}>
                    <td style={thTdStyle}>
                      {group.client && group.client.client ? group.client.client : "N/A"}
                    </td>
                    <td style={thTdStyle}>
                      {group.client && group.client.reg ? group.client.reg : clientKey}
                    </td>
                    <td style={thTdStyle}>{group.invoices.length}</td>
                    <td style={thTdStyle}>
                      <select
                        value={selectedClientManagers[clientKey] || "unknown"}
                        onChange={(e) =>
                          setSelectedClientManagers((prev) => ({
                            ...prev,
                            [clientKey]: e.target.value,
                          }))
                        }>
                        <option value="unknown">Unknown</option>
                        {usersData &&
                          usersData.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.email}
                            </option>
                          ))}
                      </select>
                    </td>
                    <td style={thTdStyle}>
                      <button
                        onClick={() => handleUpdateClientGroup(clientKey, group.invoices)}
                        style={{ cursor: "pointer" }}>
                        Update Client
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* XML Conversion Section */}
      <section style={sectionStyle}>
        <h2>XML to JSON Converter</h2>
        <p>
          Upload your XML file. The converter will extract the document data (ignoring metadata) and
          automatically trigger a download.
        </p>
        <input style={inputStyle} type="file" accept=".xml" onChange={handleXmlFileChange} />
      </section>
    </div>
  )
}

export default GetClients
